import * as React from "react";
import { debounce } from "lodash";

const { Component } = React;

import * as UserStore from "../users/UserStore";
import * as OrganizationStore from "./OrganizationStore";
import { Modal, Button, Input, Label, FormGroup, Table } from "../components/Modal";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import axios from "../general/HttpClient";

interface OrganizationRegistrationLinksState {
    links: Array<any>;
    showAddModal: boolean;
    showDisableModal: boolean;
    showEnableModal: boolean;
    showSendModal: boolean;
    linkToDisable: any;
    linkToEnable: any;
    linkToSend: any;
    membershipType: number;
    registerLinkRecipients: Array<any>;
    recipientName: string;
    recipientEmail: string;
    recipientMessage: string;
}

interface OrganizationRegistrationLinksProps {
    organization: string;
}

let contextSubscriptionId = "";

let isEmail = (email) => {
    return /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}/gim.test(email);
};

export default class OrganizationRegistrationLinks extends Component<OrganizationRegistrationLinksProps, OrganizationRegistrationLinksState> {
    constructor(props) {
        super(props);
        this.state = {
            links: [],
            showAddModal: false,
            showDisableModal: false,
            showEnableModal: false,
            showSendModal: false,
            linkToDisable: undefined,
            linkToEnable: undefined,
            linkToSend: undefined,
            membershipType: 0,
            registerLinkRecipients: [],
            recipientName: "",
            recipientEmail: "",
            recipientMessage: "",
        };
    }

    componentDidMount() {
        contextSubscriptionId = GetContext((context) => {
            this.getLinks();
        });
    }

    componentWillUnmount() {
        UnsubscribeContext(contextSubscriptionId);
    }

    getLinks() {
        OrganizationStore.ListRegisterLinks(this.props.organization).then((response) => {
            this.setState({ links: response.data || [], showAddModal: false, showDisableModal: false, showEnableModal: false });
        });
    }

    handleMembershipTypeChange = (e) => {
        var type = e.target.value;
        this.setState({ membershipType: +type });
    };

    toggleAddModal = () => {
        this.setState((prevState) => ({ showAddModal: !prevState.showAddModal }));
    };

    startCreateRegisterLink = () => {
        this.setState({
            showAddModal: true,
        });
    };

    completeCreateRegisterLink = () => {
        if (this.state.membershipType != null) {
            OrganizationStore.CreateRegisterLink(this.props.organization, this.state.membershipType).then(() => {
                this.getLinks();
            });
        }
    };

    toggleDisableModal = () => {
        this.setState((prevState) => ({ showDisableModal: !prevState.showDisableModal }));
    };

    startDisableRegisterLink = (link) => () => {
        this.setState({
            linkToDisable: link,
            showDisableModal: true,
        });
    };

    completeDisableRegisterLink = () => {
        if (this.state.linkToDisable.id) {
            OrganizationStore.DisableRegisterLink(this.props.organization, this.state.linkToDisable.id).then(() => {
                this.getLinks();
            });
        }
    };

    toggleEnableModal = () => {
        this.setState((prevState) => ({ showEnableModal: !prevState.showEnableModal }));
    };

    startEnableRegisterLink = (link) => () => {
        this.setState({
            linkToEnable: link,
            showEnableModal: true,
        });
    };

    completeEnableRegisterLink = () => {
        if (this.state.linkToEnable.id) {
            OrganizationStore.EnableRegisterLink(this.props.organization, this.state.linkToEnable.id).then(() => {
                this.getLinks();
            });
        }
    };

    copyLink = (link) => () => {
        var url = window.location.origin + "/Account/Register?token=" + link.token;
        // @ts-ignore
        navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
            if (result.state == "granted" || result.state == "prompt") {
                navigator.clipboard.writeText(url).then(
                    function () {
                        /* clipboard successfully set */
                    },
                    function () {
                        /* clipboard write failed */
                    }
                );
            }
        });
    };

    toggleSendLinkModal = () => {
        this.setState((prevState) => ({ showSendModal: !prevState.showSendModal }));
    };

    startSendRegisterLink = (link) => () => {
        this.setState({
            linkToSend: link,
            showSendModal: true,
            recipientName: "",
            recipientEmail: "",
            recipientMessage: "",
            registerLinkRecipients: [],
        });
    };

    completeSendRegisterLink = () => {
        if (this.state.linkToSend.id) {
            OrganizationStore.SendRegisterLink(this.state.linkToSend, this.state.registerLinkRecipients, this.state.recipientMessage).then(() => {
                this.setState({ showSendModal: false });
            });
        }
    };

    addRegisterLinkRecipient = () => {
        this.setState((prevState) => {
            let registerLinkRecipients = prevState.registerLinkRecipients;

            registerLinkRecipients.push({
                name: this.state.recipientName,
                email: this.state.recipientEmail,
            });

            return {
                registerLinkRecipients,
                recipientName: "",
                recipientEmail: "",
            };
        });
    };

    removeRegisterLinkRecipient = (recipient) => () => {
        this.setState((prevState) => {
            let registerLinkRecipients = prevState.registerLinkRecipients.filter((r) => r != recipient);

            return {
                registerLinkRecipients,
            };
        });
    };

    handRecipientNameChange = (e) => {
        let value = e.target.value;
        this.setState({ recipientName: value });
    };

    handRecipientEmailChange = (e) => {
        let value = e.target.value;
        this.setState({ recipientEmail: value });
    };

    handRecipientMessageChange = (e) => {
        let value = e.target.value;
        this.setState({ recipientMessage: value });
    };

    render() {
        return (
            <div>
                <Table>
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "40%" }}>Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "10%" }}>Users</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: "50%" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {this.state.links.length > 0 &&
                            this.state.links.map((link) => {
                                return (
                                    <tr key={link.id} className={link.enabled ? "hover:bg-gray-50" : "text-gray-400 hover:bg-gray-50"}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{OrganizationStore.MembershipType(link.membershipType)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{link.usersRegistered} registered</td>
                                        {link.enabled && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex gap-2">
                                                    <Button variant="outline-primary" size="sm" onClick={this.startSendRegisterLink(link)}>
                                                        Send Link
                                                    </Button>
                                                    <Button variant="outline-primary" size="sm" onClick={this.copyLink(link)}>
                                                        Copy Link
                                                    </Button>
                                                    <Button variant="warning" size="sm" onClick={this.startDisableRegisterLink(link)}>
                                                        Disable
                                                    </Button>
                                                </div>
                                            </td>
                                        )}
                                        {!link.enabled && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-500">Disabled</span>
                                                    <Button variant="outline-success" size="sm" onClick={this.startEnableRegisterLink(link)}>
                                                        Enable
                                                    </Button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        <tr className="hover:bg-gray-50">
                            <td colSpan={3} className="px-6 py-4">
                                <button 
                                    onClick={this.startCreateRegisterLink}
                                    className="text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    Add Register Link
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </Table>
                <Modal isOpen={this.state.showAddModal} onClose={this.toggleAddModal} title="Manage Organization Membership">
                    <div className="p-6 space-y-6">
                        <FormGroup>
                            <Label>Type</Label>
                            <Input type="select" name="type" value={this.state.membershipType} onChange={this.handleMembershipTypeChange}>
                                <option value="0">Staff</option>
                                <option value="1">Student</option>
                                <option value="2">Parent</option>
                                <option value="3">Guest</option>
                            </Input>
                        </FormGroup>
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={this.toggleAddModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={this.completeCreateRegisterLink}>
                                Add
                            </Button>
                        </div>
                    </div>
                </Modal>
                <Modal isOpen={this.state.showDisableModal} onClose={this.toggleDisableModal} title="Manage Organization Membership">
                    <div className="p-6 space-y-6">
                        {this.state.linkToDisable && (
                            <div>
                                Confirm disabling {OrganizationStore.MembershipType(this.state.linkToDisable.membershipType)} registration link{" "}
                                <span className="text-gray-500">({this.state.linkToDisable.id})</span>
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={this.toggleDisableModal}>
                                Cancel
                            </Button>
                            <Button variant="danger" onClick={this.completeDisableRegisterLink}>
                                Disable
                            </Button>
                        </div>
                    </div>
                </Modal>
                <Modal isOpen={this.state.showEnableModal} onClose={this.toggleEnableModal} title="Manage Organization Membership">
                    <div className="p-6 space-y-6">
                        {this.state.linkToEnable && (
                            <div>
                                Confirm enabling {OrganizationStore.MembershipType(this.state.linkToEnable.membershipType)} registration link{" "}
                                <span className="text-gray-500">({this.state.linkToEnable.id})</span>
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={this.toggleEnableModal}>
                                Cancel
                            </Button>
                            <Button variant="success" onClick={this.completeEnableRegisterLink}>
                                Enable
                            </Button>
                        </div>
                    </div>
                </Modal>
                <Modal isOpen={this.state.showSendModal} onClose={this.toggleSendLinkModal} title="Send Registration Link">
                    <div className="p-6 space-y-6">
                        <FormGroup>
                            <Label htmlFor="message">Message</Label>
                            <Input type="text" id="message" value={this.state.recipientMessage} onChange={this.handRecipientMessageChange}></Input>
                        </FormGroup>
                        {this.state.linkToSend && (
                            <div>
                                <Table>
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {this.state.registerLinkRecipients.map((recipient, i) => (
                                            <tr key={i} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{recipient.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{recipient.email}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <button 
                                                        onClick={this.removeRegisterLinkRecipient(recipient)}
                                                        className="text-red-600 hover:text-red-800 font-medium"
                                                    >
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Input type="text" value={this.state.recipientName} onChange={this.handRecipientNameChange}></Input>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Input type="email" value={this.state.recipientEmail} onChange={this.handRecipientEmailChange}></Input>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    disabled={!this.state.recipientName || !isEmail(this.state.recipientEmail)} 
                                                    onClick={this.addRegisterLinkRecipient}
                                                >
                                                    Add
                                                </Button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={this.toggleSendLinkModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={this.completeSendRegisterLink} disabled={this.state.registerLinkRecipients.length == 0}>
                                Send
                            </Button>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }
}
